import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, toUserMessage } from '@platform/api-client';
import { Modal } from '@platform/ui';

import { Alert, FormField } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { HTTP_STATUS } from '@/shared/constants/app/http.constant';
import { UserStatus } from '@/shared/constants/auth/auth.constant.ts';
import type { LoginRequest } from '@/shared/types/auth.type';
import {
  DEFAULT_REDIRECT,
  resolveRedirectTarget,
  REDIRECT_PARAM,
  type RedirectStateFrom,
} from '@/shared/libs/redirect';
import { rememberOnboardingReturn } from '@/shared/libs/onboarding-return';
import { markPostLoginVerificationCheck } from '@/shared/libs/post-login-verify';
import { useLogin } from '../api/use-auth.mutation';
import { ResendVerificationButton } from '../components/ResendVerificationButton';
import { SignupEmailRecovery } from '../components/SignupEmailRecovery';
import {
  businessLoginSchema,
  personalLoginSchema,
  type LoginFormValues,
} from '../schemas/auth.schemas';
import {
  LOGIN_TAB_PARAM,
  readRememberedOrgSlug,
  rememberOrgSlug,
  type LoginTab,
} from '../utils/login-tab';
import {
  EMAIL_SHAPED,
  sanitizeLoginIdentifier,
  sanitizeOrgSlug,
  splitLoginIdentifier,
} from '../utils/login-identifier';
import { legacyToLoginId } from '../utils/legacy-login-id';

/**
 * 화면 입력 → 로그인 요청. 개인 탭은 구버전 아이디를 신 아이디 규칙으로 바꿔 `loginId` 에 싣고,
 * 바뀐 경우 원문을 `legacyLoginId` 로 함께 보낸다 — 보정·중복 회피로 번호가 붙은 계정은 서버가 원문으로 찾는다.
 */
function toLoginRequest(values: LoginFormValues, isBusiness: boolean): LoginRequest {
  if (isBusiness)
    return {
      loginId: values.loginId.trim().toLowerCase(),
      password: values.password,
      orgSlug: values.orgSlug.trim().toLowerCase(),
    };
  const raw = values.loginId.trim();
  const loginId = legacyToLoginId(raw);
  return {
    loginId,
    password: values.password,
    ...(raw !== loginId ? { legacyLoginId: raw } : {}),
  };
}

interface LocationState {
  from?: RedirectStateFrom;
}

/**
 * 이메일 미인증 도움 모달. 열 때의 로그인 입력값을 붙잡아 둔다 — 재발송(가입 주소로)과
 * 가입정보 확인이 모두 이 계정을 대상으로 하기 때문(서버 전송 형태로 정규화해 보관).
 */
interface VerificationHelp {
  mode: 'resend' | 'recover';
  account: LoginRequest;
  /** 재발송을 한 번이라도 했는지 — 그 뒤에야 "가입 정보 확인" 안내를 보여준다. */
  resent?: boolean;
}

/**
 * 로그인 View.
 *
 * ── 개인 / 기업 탭 ──────────────────────────────────────────────
 * **이메일로는 로그인할 수 없다.** 개인은 아이디 한 칸, 기업은 아이디 + 회사코드
 * 두 칸을 받아 서버가 `odin@howcost` 로 조립한다. 한 칸에 통째로 받지 않는 이유는
 * `odin@howcost` 가 눈으로 이메일과 구분되지 않아서다 — 사용자가 사내 이메일을
 * 치는 혼동을 입력 구조로 막는다.
 *
 * 두 칸은 조립 순서(아이디 → `@`회사코드)대로 세로로 놓고 회사코드 칸에 `@` 를
 * 붙여 둔다. 칸이 나뉜 이유가 형태를 보여주기 위한 것이라 순서가 뒤집히면 무의미하다.
 *
 * 탭은 URL 쿼리(`?type=business`)로도 지정된다. 기업 고객에게 전용 링크를 안내할 수
 * 있어야 하기 때문. 라우트를 쪼개지 않은 것은 복귀 경로(`?from=`)·가드 연동·에러
 * 처리 같은 공통 로직이 이 컴포넌트 하나에 모여 있어서다.
 *
 * ── 복귀 경로 결정 우선순위 ─────────────────────────────────────
 *   1) AuthGuard 가 넘긴 location.state.from
 *   2) ?from=... 쿼리 파라미터 (api-client.onUnauthorized 가 넘기는 값)
 *   3) '/'
 *
 * ── 에러 처리 ───────────────────────────────────────────────────
 *   401: 아이디/비밀번호 오류 → 일반 메시지
 *   403: 이메일 미인증(INACTIVE) / 정지(SUSPENDED) → 서버 메시지 그대로 노출
 */
export function LoginView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState<LoginTab>(() =>
    searchParams.get(LOGIN_TAB_PARAM) === 'business' ? 'business' : 'personal',
  );
  const [form, setForm] = useState<LoginFormValues>(() => ({
    // 사내 공용 PC 를 고려해 회사코드만 기억해 둔다(아이디·비밀번호는 저장하지 않는다).
    orgSlug: readRememberedOrgSlug(),
    loginId: '',
    password: '',
  }));
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof LoginFormValues, string>>
  >({});
  /**
   * 아이디 칸에 들어온 값이 아이디가 아닐 때의 안내.
   *   'email'    — 이메일을 그대로 침
   *   'business' — 개인 탭에서 `아이디@회사코드` 를 침
   * 서버를 타지 않으므로 계정 열거와 무관하다.
   */
  const [identifierNotice, setIdentifierNotice] = useState<
    'email' | 'business' | null
  >(null);
  const [invalidTyped, setInvalidTyped] = useState<
    Partial<Record<'loginId' | 'orgSlug', boolean>>
  >({});
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [help, setHelp] = useState<VerificationHelp | null>(null);

  const { mutate: login, isPending, error, reset } = useLogin();

  const isBusiness = tab === 'business';

  // 탭이 바뀌면 이전 탭의 검증 결과가 남지 않게 정리한다.
  useEffect(() => {
    setFieldErrors({});
    setIdentifierNotice(null);
    setInvalidTyped({});
    setHelp(null);
    reset();
  }, [tab, reset]);

  const stateFrom = (location.state as LocationState | null)?.from;
  const from = resolveRedirectTarget({
    stateFrom,
    queryFrom: searchParams.get(REDIRECT_PARAM),
  });

  const clearFieldError = (key: keyof LoginFormValues) => {
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  /**
   * 아이디 칸 값으로 띄울 안내. 값을 고치지 않으므로 타이핑 중에 매번 돌려도 된다.
   * 개인 탭의 이메일 모양 입력은 미리 막지 않는다 — 구버전 이메일형 아이디일 수 있어 한 번은 로그인을
   * 시도하고, 실패하면 그때 안내한다(handleSubmit).
   */
  const noticeFor = (value: string): 'email' | 'business' | null => {
    if (isBusiness && EMAIL_SHAPED.test(value.trim())) return 'email';
    if (!isBusiness && splitLoginIdentifier(value)) return 'business';
    return null;
  };

  /**
   * 기업 탭 아이디 칸에 `odin@howcost` 를 통째로 넣은 경우 두 칸으로 나눈다.
   * 타이핑 도중에 나누면 `@` 뒤 글자가 계속 아이디 칸에 쌓이므로, 값이 확정되는
   * 시점(포커스 이탈·제출)에서만 부른다.
   */
  const splitIntoFields = (value: string) => {
    if (!isBusiness) return;
    const split = splitLoginIdentifier(value);
    if (!split) return;
    setForm((prev) => ({ ...prev, ...split }));
    clearFieldError('orgSlug');
    setIdentifierNotice(null);
  };

  /** 개인 탭에서 `아이디@회사코드` 를 친 경우 — 기업 탭으로 옮겨 담는다. */
  const switchToBusinessTab = () => {
    const split = splitLoginIdentifier(form.loginId);
    if (split) setForm((prev) => ({ ...prev, ...split }));
    setTab('business');
  };

  const onChange =
    (key: keyof LoginFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // 개인 탭 아이디는 고치지 않고 받는다 — 구버전 아이디(대문자·점·한글·이메일형)를 그대로 친 사람이
      // 있어서다. 제출할 때 신 아이디 규칙으로 바꿔 보낸다(legacyToLoginId). 한글 입력 조합도 깨지지 않는다.
      const value =
        key === 'orgSlug'
          ? sanitizeOrgSlug(e.target.value)
          : key === 'loginId' && isBusiness
            ? sanitizeLoginIdentifier(e.target.value)
            : e.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
      clearFieldError(key);
      if (key === 'loginId') setIdentifierNotice(noticeFor(value));
      if (key === 'orgSlug' || (key === 'loginId' && isBusiness)) {
        // 대문자는 소문자로 바뀔 뿐 지워진 게 아니므로 안내 대상이 아니다.
        setInvalidTyped((prev) => ({
          ...prev,
          [key]: value !== e.target.value.toLowerCase(),
        }));
      }
      // 다른 계정을 치기 시작하면 붙잡아 둔 계정 기준의 도움 모달은 의미가 없다.
      if (key !== 'password') setHelp(null);
      if (error) reset();
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // 다시 로그인하면 새 결과(다른 오류 포함)를 보여줘야 하므로 모달을 닫는다.
    setHelp(null);

    // 기업 탭에 이메일을 그대로 친 경우는 서버를 타지 않는다. 계정 열거 방지 때문에 서버는
    // 무조건 "아이디 또는 비밀번호가 올바르지 않습니다"로 답할 수밖에 없어,
    // 사용자가 왜 실패했는지 영영 알 수 없다. (개인 탭은 구버전 이메일형 아이디라 시도 후 안내)
    if (isBusiness && EMAIL_SHAPED.test(form.loginId.trim())) {
      setIdentifierNotice('email');
      setFieldErrors({ loginId: '이메일로는 로그인할 수 없습니다.' });
      return;
    }

    // 개인 탭에 기업 아이디를 넣으면 서버는 없는 계정으로 답한다 — 탭부터 안내한다.
    if (!isBusiness && splitLoginIdentifier(form.loginId)) {
      setIdentifierNotice('business');
      setFieldErrors({
        loginId: '기업 회원 아이디입니다. [기업 회원] 탭에서 로그인해 주세요.',
      });
      return;
    }

    // Enter 로 바로 제출하면 blur 가 없어 여기서 나눈다.
    const split = isBusiness ? splitLoginIdentifier(form.loginId) : null;
    const values = split ? { ...form, ...split } : form;
    if (split) setForm(values);

    const schema = isBusiness ? businessLoginSchema : personalLoginSchema;
    const parsed = schema.safeParse({
      loginId: values.loginId,
      password: values.password,
      ...(isBusiness ? { orgSlug: values.orgSlug } : {}),
    });
    if (!parsed.success) {
      // 두 스키마의 필드 집합이 달라(개인에는 orgSlug 가 없다) flatten 결과 타입이
      // 좁혀진다 — 화면은 세 칸을 한 모양으로 다루므로 여기서 넓혀 받는다.
      const flat = parsed.error.flatten().fieldErrors as Partial<
        Record<keyof LoginFormValues, string[]>
      >;
      setFieldErrors({
        orgSlug: flat.orgSlug?.[0],
        loginId: flat.loginId?.[0],
        password: flat.password?.[0],
      });
      return;
    }

    login(
      toLoginRequest(values, isBusiness),
      {
        onError: () => {
          // 개인 탭 이메일 모양 입력이 실패했으면 이제 안내한다 — 이메일로 로그인하려던 것일 가능성이 크다.
          if (!isBusiness && EMAIL_SHAPED.test(values.loginId.trim()))
            setIdentifierNotice('email');
        },
        onSuccess: (data) => {
          if (isBusiness) rememberOrgSlug(values.orgSlug);

          // Owner 가 만든 구성원 계정의 첫 로그인 — 온보딩을 마쳐야 아무것도 할 수
          // 없다(Gateway 가 막는다). 원래 가려던 곳으로 보내면 곧바로 403 을 맞는다.
          if (data.user.status === UserStatus.PENDING_ONBOARDING) {
            // 가려던 곳은 온보딩을 마친 뒤로 미룬다(예: XCOST 로그인 동의 화면).
            if (from !== DEFAULT_REDIRECT) rememberOnboardingReturn(from);
            navigate(PATHS.ONBOARDING, { replace: true });
            return;
          }

          // 약관 개정으로 다시 동의해야 하는 계정 — 동의 전에는 Gateway 가 거의 모든 요청을 막는다.
          if (data.user.reconsent?.length) {
            if (from !== DEFAULT_REDIRECT) rememberOnboardingReturn(from);
            navigate(PATHS.RECONSENT, { replace: true });
            return;
          }

          // 기업 미검증 계정이면 다음 진입 시 1회 기업검증 화면으로 유도(one-shot).
          markPostLoginVerificationCheck();
          navigate(from, { replace: true });
        },
      },
    );
  };

  // 403 이메일 미인증 안내 여부.
  const isInactiveAccountError =
    error instanceof ApiError &&
    error.status === HTTP_STATUS.FORBIDDEN &&
    (error.code === 'AUTH_EMAIL_NOT_VERIFIED' ||
      error.code === 'EMAIL_NOT_VERIFIED' ||
      /이메일\s*인증/.test(error.message));

  // Modal 은 onClose 가 바뀌면 포커스를 첫 칸으로 되돌린다 — 참조를 고정해 입력 중 커서가 튀지 않게.
  const closeVerificationHelp = useCallback(() => setHelp(null), []);

  /** 미인증 안내에서 도움 모달을 연다. 지금 로그인을 시도한 계정을 대상으로 붙잡는다. */
  const openVerificationHelp = () => {
    setHelp({ mode: 'resend', account: toLoginRequest(form, isBusiness) });
  };

  const tabButton = (value: LoginTab, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setTab(value)}
      aria-selected={tab === value}
      role="tab"
      className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
        tab === value
          ? 'bg-brand-500 text-white'
          : 'text-adobe-muted hover:text-adobe-dark dark:text-neutral-400 dark:hover:text-neutral-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-7">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
          로그인
        </h1>
        <p className="mt-1 text-sm text-adobe-muted dark:text-neutral-400">
          {isBusiness
            ? '아이디와 회사코드로 로그인하세요.'
            : '아이디로 로그인하세요.'}
        </p>
      </header>

      <div
        role="tablist"
        className="mb-6 flex gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800"
      >
        {tabButton('personal', '개인 회원')}
        {tabButton('business', '기업 회원')}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {isBusiness && (
          <>
            <FormField
              label="회사코드"
              type="text"
              name="orgSlug"
              // 브라우저가 회사코드+아이디를 한 계정으로 묶도록 organization 힌트를 준다.
              autoComplete="organization"
              placeholder="howcost"
              prefix="@"
              required
              value={form.orgSlug}
              onChange={onChange('orgSlug')}
              error={fieldErrors.orgSlug}
              hint={
                invalidTyped.orgSlug && (
                  <span className="text-amber-700 dark:text-amber-300">
                    회사코드는 영문, 숫자만 사용할 수 있습니다.
                  </span>
                )
              }
            />

            {!!form.loginId && !!form.orgSlug && (
              <p className="text-xs text-adobe-muted dark:text-neutral-500">
                로그인 ID:{' '}
                <b className="font-mono text-adobe-dark dark:text-neutral-100">
                  {`${form.loginId.trim().toLowerCase()}@${form.orgSlug
                  .trim()
                  .toLowerCase()}`}
                </b>
              </p>
            )}
          </>
        )}

        <FormField
          label="아이디"
          type="text"
          name="loginId"
          autoComplete="username"
          placeholder=""
          required
          value={form.loginId}
          onChange={onChange('loginId')}
          onBlur={(e) => splitIntoFields(e.target.value)}
          error={fieldErrors.loginId}
          hint={
            invalidTyped.loginId && (
              <span className="text-amber-700 dark:text-amber-300">
                아이디는 영문, 숫자만 사용할 수 있습니다.
              </span>
            )
          }
        />

        {identifierNotice === 'email' && (
          <Alert tone="warning">
            이메일로는 로그인할 수 없습니다. 가입 시 정한 아이디를 입력해 주세요.
            <br />
            <Link
              to={PATHS.FIND_LOGIN_ID}
              className="text-xs font-semibold underline underline-offset-2"
            >
              아이디 찾기
            </Link>
          </Alert>
        )}

        {identifierNotice === 'business' && (
          <Alert tone="warning">
            <b>아이디@회사코드</b> 형태는 기업 회원 계정입니다.
            <br />
            <button
              type="button"
              onClick={switchToBusinessTab}
              className="rounded-sm text-xs font-semibold underline underline-offset-2 hover:opacity-80"
            >
              기업 회원으로 전환
            </button>
          </Alert>
        )}

        <FormField
          label="비밀번호"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={form.password}
          onChange={onChange('password')}
          // Caps Lock 키 자체를 누를 때는 OS 마다 keydown/keyup 중 한쪽만 바뀐 상태를
          // 보고하므로 둘 다 확인한다.
          onKeyDown={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
          onKeyUp={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
          onBlur={() => setCapsLockOn(false)}
          error={fieldErrors.password}
          hint={
            capsLockOn && (
              <span className="text-amber-700 dark:text-amber-300">
                Caps Lock이 켜져 있습니다.
              </span>
            )
          }
        />

        {/*
          미인증은 실패가 아니라 "한 단계 남은 상태"다 — 빨간 오류 대신 차분한 안내 카드로 보여준다.
          비밀번호 오류 등 진짜 실패만 error 톤.
        */}
        {error && isInactiveAccountError && (
          <div
            role="status"
            className="flex gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-700 dark:bg-neutral-800/50"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-brand-500"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <div className="min-w-0 break-keep">
              <p className="text-sm font-semibold text-adobe-dark dark:text-neutral-100">
                {toUserMessage(error)}
              </p>
              <p className="mt-0.5 text-xs text-adobe-muted dark:text-neutral-400">
                메일함(스팸함 포함)에서 인증 링크를 확인해 주세요.
              </p>
              <button
                type="button"
                onClick={openVerificationHelp}
                className="mt-2 rounded-sm text-xs font-semibold text-brand-600 underline underline-offset-2 hover:opacity-80 dark:text-brand-400"
              >
                인증 메일이 도착하지 않은 경우
              </button>
            </div>
          </div>
        )}
        {error && !isInactiveAccountError && (
          <Alert tone="error">{toUserMessage(error)}</Alert>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
        >
          {isPending ? '로그인 중…' : '로그인'}
        </button>
      </form>

      {/*
        여러 단계(재발송 → OTP → 이메일 확인·수정)라 모달로 띄운다. 바깥 클릭으로는 닫지 않는다 —
        OTP 입력 중에 실수로 닫히면 진행 상태가 날아간다(닫기 버튼·ESC 로만 닫힘).
      */}
      <Modal
        open={!!help}
        onClose={closeVerificationHelp}
        title={help?.mode === 'recover' ? '가입 정보 확인' : '인증 메일 재발송'}
        description={
          // 공용 Modal 의 설명은 글자 단위로 줄바꿈된다 — 이 화면에서만 어절 단위로.
          // 줄마다 블록 + text-balance: 폭이 모자라 넘어가도 끝 단어 하나만 남지 않게 줄 길이를 고르게.
          // (<br> 로 나누면 브라우저가 줄 맞춤을 하지 않는다)
          <span className="block break-keep [&>span]:block [&>span]:text-balance">
            {help?.mode === 'recover' ? (
              <>
                <span>가입한 휴대폰 번호를 입력해 주세요.</span>
                <span>확인 후 가입된 이메일이 표시됩니다.</span>
              </>
            ) : (
              <span>가입 시 입력한 이메일로 인증 링크를 재발송합니다.</span>
            )}
          </span>
        }
        closeOnBackdrop={false}
      >
        {help?.mode === 'resend' && (
          <>
            <ResendVerificationButton
              account={help.account}
              onSent={() => setHelp((h) => (h ? { ...h, resent: true } : h))}
            />
            {/* 다시 받아도 안 오면 가입 이메일 오타일 수 있다 — 재발송을 한 번 해 본 뒤에 다음 단계를 보여준다. */}
            {help.resent && (
              <p className="mt-4 break-keep border-t border-neutral-200 pt-4 text-xs text-adobe-muted dark:border-neutral-700 dark:text-neutral-400">
                메일이 도착하지 않는 경우 가입 시 입력한 이메일 주소를 확인해 주세요.{' '}
                <button
                  type="button"
                  onClick={() => setHelp({ ...help, mode: 'recover' })}
                  className="rounded-sm font-semibold text-brand-600 underline underline-offset-2 hover:opacity-80"
                >
                  가입 정보 확인
                </button>
              </p>
            )}
          </>
        )}
        {help?.mode === 'recover' && (
          <>
            <SignupEmailRecovery
              loginId={help.account.loginId}
              orgSlug={help.account.orgSlug}
            />
            <button
              type="button"
              onClick={() => setHelp({ ...help, mode: 'resend' })}
              className="mt-4 rounded-sm text-xs text-adobe-muted underline underline-offset-2 hover:text-adobe-dark dark:text-neutral-400"
            >
              인증 메일 재발송으로 돌아가기
            </button>
          </>
        )}
      </Modal>

      <div className="mt-6 flex items-center justify-between text-sm">
        <div className="flex gap-3">
          <Link
            to={PATHS.FIND_LOGIN_ID}
            className="text-adobe-muted transition-colors hover:text-adobe-dark dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            아이디 찾기
          </Link>
          <Link
            to={PATHS.REQUEST_PASSWORD_RESET}
            className="text-adobe-muted transition-colors hover:text-adobe-dark dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            비밀번호 찾기
          </Link>
        </div>
        <span className="text-adobe-muted dark:text-neutral-400">
          {/* 기업 탭에서 넘어가면 가입 유형도 기업으로 고른 채 연다. */}
          <Link
            to={
              isBusiness
                ? `${PATHS.SIGNUP}?${LOGIN_TAB_PARAM}=business`
                : PATHS.SIGNUP
            }
            className="font-semibold text-primary hover:underline"
          >
            회원가입
          </Link>
        </span>
      </div>

      {isBusiness && (
        <p className="mt-4 text-xs text-adobe-muted dark:text-neutral-500">
          회사코드를 모르는 경우 소속 기업의 관리자에게 문의해 주세요.
        </p>
      )}
    </div>
  );
}
